import {
	ColumnDef,
	createDefaultCellDef,
	createDefaultFooterCellDef,
	createDefaultHeaderCellDef,
	DEFAULT_FORMATS,
	TableModel,
} from '../../components/table/table/table.model';
import { Sort } from '../../components/table/table/table.component';
import { ColumnOptions, ColumnType, createMeasureColumn, DataRow, DataTreeTableComponent, TOTAAL_LABEL } from './data-tree-table';
import { Attributes, BaseDashboardConfig } from '../base-dashboard/base-dashboard-config';
import { DashboardContext } from '../base-dashboard/dashboard-context';
import { Observable } from 'rxjs';
import { ExportDataOptions, ExportFilter, FilterExpression } from '../../../services/data.service';
import { AfterViewInit, Directive, effect, inject, signal, ViewChild } from '@angular/core';
import { UrlTree } from '@angular/router';
import { UserRouteData } from '../../../services/user-routes.service';
import { VPartitionData } from '../vbarchart-table/vbar-series/vbar-series.component';
import { VbarSeriesData } from '../vbarchart-table/vbar-batch/vbar-batch.component';
import { VBarExportColumnDef } from '../vbarchart-table/vbarchart-table.component';
import { attrLabel } from '../../../services/labels';
import { isFirstAtLevel, Level, Path } from '../../../services/data-tree';
import { DisplayService } from '../../../services/display.service';
import { switchMap } from 'rxjs/operators';
import { LabelCellComponent } from '../../components/table/cells/label-cell/label-cell.component';
import { difference, isFunction, last } from 'lodash-es';
import { PageStateService } from '../../../services/page-state.service';
import {
	LeerlingAantalCellComponent,
	LeerlingAantalCellData,
} from '../../components/table/cells/leerling-aantal-cell/leerling-aantal-cell.component';
import { CategoryFields, getGroups } from '../../../services/metadata';
import { GroeperingService } from '../../../services/groepering.service';
import { ExportOptions } from '../../../services/export.service';
import { Att, AttrPath } from '@cumlaude/metadata';

function getFirstGroupColumnName(model: TableModel<DataRow<any>>): string | undefined {
	return model?.columnDefs[0].type === ColumnType.GROUP ? model.columnDefs[0].column : undefined;
}

const defaultGroupColumnOptions: ColumnOptions<any, any> = {
	component: LabelCellComponent,
	dataType: 'string',
	visible: true,
};

@Directive()
export abstract class DataTreeTableConfig<I extends Attributes, A extends Attributes> extends BaseDashboardConfig<I, A> implements AfterViewInit {
	protected readonly displayService: DisplayService = inject(DisplayService);
	protected readonly pageStateService: PageStateService = inject(PageStateService);
	protected readonly groeperingService = inject(GroeperingService);

	readonly allAvailableGroups = signal<CategoryFields[]>([]);

	lockedColumn = this.qp.signal('lockedColumn');

	constructor() {
		super();
		effect(() => {
			this.lockedColumn();
			this.filterService.refresh();
		});
	}

	/**
	 * Haalt de grenzen aan het groeperen uit de dashboard-registry, zodat ze op één plek staan: de
	 * AI-chat krijgt ze via het gegenereerde manifest en het dashboard leest ze hier.
	 *
	 * Dit gebeurt in ngAfterViewInit en niet in de constructor, omdat veld-initialisaties van de
	 * subklassen daarna pas draaien: Examencijfers erft zijn `availableGroups` van Cijferoverzicht en
	 * zou de registrywaarde anders overschrijven.
	 */
	private leesGroeperingsgrenzenUitRegistry() {
		const data: UserRouteData | undefined = this.routeData();
		if (data?.maxGroeperingen !== undefined) this.flexibleMaxGroups = data.maxGroeperingen;

		if (data?.toegestaneGroeperingen) {
			this.availableGroups = data.toegestaneGroeperingen.map((att) => att.split('.') as AttrPath);
			this.allowAllAvailableGroups = false;
		}
	}

	ngAfterViewInit() {
		this.leesGroeperingsgrenzenUitRegistry();

		if (!this.allowAllAvailableGroups) return;

		this.allAvailableGroups.set(this.filterAllGroups(getGroups(this.factTable, this.attOverrides)));
	}

	createGroupingColumns(context: DashboardContext<I, A, DataTreeTableConfig<I, A>>): ColumnDef<DataRow<A>>[] {
		const groupNames = context.groupNames;

		const flexStart = this.getFixedBeforeGroups();
		const flexEnd = groupNames.length - this.getFixedAfterGroups();
		const flexibleGroups = flexEnd - flexStart;

		const lockedColumn = this.lockedColumn();
		const lockedColumnIx = lockedColumn ? groupNames.indexOf(lockedColumn) : -1;
		const isFlexible = (ix: number) => flexStart <= ix && ix < flexEnd;
		const columns = groupNames.map((attr, ix) =>
			this.createGroupingColumn(attr, ix + 1, isFlexible(ix), ix <= lockedColumnIx, ix === lockedColumnIx, {
				visible: !this.groupsAsTitles.includes(ix),
			})
		);

		if (Math.max(flexibleGroups, 0) < this.flexibleMaxGroups && !this.isHistorieBatchVariant())
			columns.splice(flexEnd, 0, this.createAddGroupColumn());

		return columns;
	}

	/**
	 * Geeft aan of dashboard van het sub-type historie batch is. Bepaalt waar het plus-icoon komt.
	 */
	isHistorieBatchVariant() {
		return false;
	}

	/**
	 * Geeft het aantal vaste groeperingen aan het einde van de groeperingskolommen aan.
	 * Gebruik deze om de waarde te wijzigen op basis van extra randvoorwaarden.
	 */
	protected getFixedAfterGroups() {
		return this.fixedAfterGroups;
	}

	/**
	 * Geeft het aantal vaste groeperingen aan het begin van de groeperingskolommen aan.
	 * Gebruik deze om de waarde te wijzigen op basis van extra randvoorwaarden.
	 */
	protected getFixedBeforeGroups() {
		return this.fixedBeforeGroups;
	}

	/**
	 * NB moet een stabiele lijst opleveren (dus zo nodig memoizen)
	 */
	public getAvailableGroups() {
		return this.availableGroups;
	}

	private createAddGroupColumn(): ColumnDef<DataRow<A>> {
		return {
			column: 'addGroup',
			header: {
				...createDefaultHeaderCellDef('addGroup'),
				getValue: () => undefined,
				headerType: 'addGroup',
			},
			body: {
				...createDefaultCellDef('addGroup'),
			},
			footer: {
				...createDefaultFooterCellDef(),
			},
			visible: true,
			changeGroups: true,
		};
	}

	protected createGroupingColumn(
		group: string,
		ix: number,
		changeGroups: boolean,
		sticky: boolean,
		locked: boolean,
		options: Partial<ColumnOptions<I, A>> = {}
	): ColumnDef<DataRow<A>> {
		const { component, format, header, headerClass, headerTooltip, clickHandler, dataType, visible } = {
			...defaultGroupColumnOptions,
			...options,
		};
		const attrPath = <AttrPath>group.split('.');
		return {
			column: group,
			header: {
				...createDefaultHeaderCellDef<DataRow<A>>(group, header ?? attrLabel(attrPath)),
				class: headerClass ?? 'group-column',
				headerType: 'groepering',
				tooltip: headerTooltip,
			},
			body: {
				...createDefaultCellDef<DataRow<A>>(group),
				component,
				getValue: ({ _path }) => this.displayService.display(_path[ix].k, attrPath),
				isVisible: ({ _path }) => isFirstAtLevel(_path, ix),
				getClassName: (row) => (row._path[ix].k === null ? 'empty' : undefined),
				format: isFunction(format) ? (row) => format(row._path) : format,
				dataType: isFunction(dataType) ? (row) => dataType(row._path) : dataType,
				clickHandler: clickHandler ? ({ _path }) => clickHandler(_path) : undefined,
			},
			footer: {
				...createDefaultFooterCellDef<DataRow<A>>(),
				getValue: () => (ix === 1 && this.showTotaalFooter() ? TOTAAL_LABEL : null),
			},
			type: ColumnType.GROUP,
			visible,
			sticky,
			locked,
			changeGroups,
		};
	}

	createMeasureColumns(_context: DashboardContext<I, A, DataTreeTableConfig<I, A>>): ColumnDef<DataRow<A>>[] {
		return [];
	}

	/**
	 * We gaan ervan uit dat alle rows clickable zijn als de eerste een url oplevert.
	 */
	areRowsClickable(context: DashboardContext<I, A, DataTreeTableConfig<I, A>>): boolean {
		const path = context.dataRoot?.r[0];
		if (path === undefined) return false;

		return Boolean(this.getRowClickUrl(context, path));
	}

	enrichTableModel(_context: DashboardContext<I, A, DataTreeTableConfig<I, A>>, _tableModel: TableModel<DataRow<A>>) {
		//Override voor child
	}

	getDefaultSort(tableModel: TableModel<DataRow<A>>): Sort {
		return { active: getFirstGroupColumnName(tableModel)!, direction: 'asc' };
	}

	columnOrder: ColumnType[] = [ColumnType.GROUP, ColumnType.BARCHART, ColumnType.MEASURE];

	/**
	 * Beschikbare default groeperingen waar een gebruiker uit kan kiezen in de groeperingsdropdown.
	 */
	availableGroups: AttrPath[] = [];

	/**
	 * Aantal vaste groepen aan het begin van de lijst met groeperingskolommen.
	 */
	fixedBeforeGroups: number = 0;

	/**
	 * Aantal vaste groepen aan het einde van de lijst met groeperingskolommen.
	 */
	fixedAfterGroups: number = 0;

	/**
	 * Het maximale aantal groeperingen dat gekozen mag worden.
	 */
	flexibleMaxGroups: number = Number.MAX_VALUE;

	/**
	 * Mogen groeperingen verwijderd worden of altijd verplicht aanwezig.
	 */
	flexibleGroupsRemovable: boolean = true;

	/**
	 * Is de alle groeperingen optie beschikbaar.
	 */
	allowAllAvailableGroups: boolean = true;

	/**
	 * De groepen op deze plekken worden niet als groeperingskolom getoond maar als titel van een table (of van een groep tables).
	 * NB posities moeten refereren naar bestaande tableGroups.
	 */
	groupsAsTitles: number[] = [];

	getAlternatingGroupLevel() {
		return 1;
	}

	showTotaalFooter() {
		return true;
	}

	// Inject het gebruikte DataTreeTableComponent zodat we dit kunnen gebruiken voor de tabel-export.
	// NB subclasses van DataTreeTableComponent moeten met "providers" in hun decorator aangeven dat ze een DataTreeTableComponent zijn,
	// want de dependency injection van Angular heeft geen weet van inheritance.
	@ViewChild(DataTreeTableComponent)
	protected dataTreeTableComponent?: DataTreeTableComponent<I, A, DataTreeTableConfig<I, A>>;

	override exportAsTable(exportOptions: ExportOptions, title: string, filters: ExportFilter[]): Observable<Blob> {
		if (this.dataTreeTableComponent) return this.dataTreeTableComponent.exportAsTable(exportOptions, title, filters);
		return super.exportAsTable(exportOptions, title, filters);
	}

	override exportToPng(options: ExportDataOptions): Observable<Blob> {
		return this.dataTreeTableComponent!.forceTotalRender().pipe(switchMap(() => super.exportToPng(options)));
	}

	override exportToPdf(options: ExportDataOptions): Observable<Blob> {
		return this.dataTreeTableComponent!.forceTotalRender().pipe(switchMap(() => super.exportToPdf(options)));
	}

	getExportTablePartitionColumns(partition: VPartitionData, ix: number, getValue: (rowModel: DataRow<A>) => any): VBarExportColumnDef<A>[] {
		return [
			{
				name: partition.label,
				type: partition.measure.type,
				format: partition.measure.format ?? DEFAULT_FORMATS[partition.measure.type],
				getValue,
				unwrapChildren: (seriesData: VbarSeriesData) => seriesData.series[ix].measure,
			},
		];
	}

	getRowClickUrl(context: DashboardContext<I, A, DataTreeTableConfig<I, A>>, path: Path<A, number[]>): UrlTree | undefined {
		const selection = this.createSelection(context, path);
		return this.urlService.getSelectionUrlTree(selection);
	}

	getZoomClickUrl(
		atts: Att[],
		groups: string[],
		path: Level<A, number[]>[],
		options: { ignoreFirst?: number; singleGroup?: boolean } = {}
	): UrlTree | undefined {
		const zoomAtts = atts.filter((att) => this.userService.isAttAllowed(att));
		const nextGroup = options.singleGroup ? zoomAtts[zoomAtts.indexOf(<Att>last(groups)) + 1] : difference(zoomAtts, groups)[0];
		if (nextGroup) {
			const groupValues = path
				.slice(1)
				.map((level, ix) => ({ name: <Att>groups[ix], value: level.k }))
				.filter((level) => this.filterService.configs[level.name] !== undefined);
			const targetGroups = options.singleGroup ? [nextGroup] : [...groups.slice(options.ignoreFirst ?? 0), nextGroup];
			const groupPaths = targetGroups.map((group) => <AttrPath>group.split('.'));
			const params = this.filterService.convertToFilters(groupValues, this.qp.encodeGroupQueryParam(groupPaths));
			return this.urlService.getRedirectUrlTree([], params);
		} else {
			return undefined;
		}
	}

	createLeerlingColumn(
		column: string,
		getDisplayAantal: (path: Path<A, number[]>) => number,
		options: Partial<ColumnOptions<I, A>> & { context: ColumnOptions<I, A>['context'] },
		getLlnAantal?: (path: Path<A, number[]>) => number,
		extraFilters?: FilterExpression[]
	) {
		const { context } = options;
		const selectionAllowed = this.isSelectionAllowed(context!);
		const getSize = getLlnAantal ?? getDisplayAantal;
		const possibleOverride = extraFilters ? { extraFilters } : {};
		const clickHandler: (path: Path<A, number[]>) => void = (path) => {
			const selection = this.createSelection(context!, path, { getSize, ...possibleOverride });
			this.urlService.navigateToSelection(selection);
		};
		const getValue: (path: Path<A, number[]>) => LeerlingAantalCellData = (path) => {
			const selection = this.createSelection(context!, path, { getSize, ...possibleOverride });
			return { selection, displayAantal: getDisplayAantal(path) };
		};
		return createMeasureColumn(column, getValue, {
			component: LeerlingAantalCellComponent,
			...options,
			clickHandler: selectionAllowed ? clickHandler : undefined,
		});
	}

	/**
	 * Geeft de optie voor dashboards om de alle groeperingen opties te filteren.
	 */
	filterAllGroups(groups: CategoryFields[]) {
		return groups;
	}
}
